import { FilterMatchMode, FilterOperator } from 'primereact/api'

/**
 * Force a date value to GMT timezone
 * Handles the issue where JavaScript dates are relative to the browser's timezone
 * which can vary (e.g., GMT vs BST in the UK)
 */
export const forceToGmt = (value) => {
    const timezoneIdentifier = "GMT"
    const gmtIdentifier = "GMT+0000 (Greenwich Mean Time)"
    const originalValue = String(value)
    const gmtPosition = Number(originalValue.indexOf(gmtIdentifier))

    if (gmtPosition === -1) {
        // It was not a GMT value
        const timezonePosition = Number(originalValue.indexOf(timezoneIdentifier))
        return new Date(originalValue.substring(0, timezonePosition) + gmtIdentifier)
    }

    // Otherwise it is GMT already, we don't need to mutate
    return value
}

/**
 * Process PrimeReact filter constraints and convert them to a query string format
 * Handles date conversion to GMT and string uppercasing
 * Respects the filter operator (AND/OR) from PrimeReact
 */
export const processFilterConstraints = (filters) => {
    if (!filters) return null

    const result = Object.entries(filters)
        .map(([field, fieldFilter]) => {
            const constraints = fieldFilter.constraints
                ?? [{ value: fieldFilter.value, matchMode: fieldFilter.matchMode }]

            // Get the operator for this field (defaults to AND if not specified)
            const operator = fieldFilter.operator === FilterOperator.OR ? ' or ' : ' and '

            // Process each constraint for this field
            const fieldConditions = constraints
                .map(constraint => {
                    let value = constraint.value

                    if (value != null) {
                        // Handle object values (shouldn't happen, but defensive programming)
                        if (typeof value === 'object' && !(value instanceof Date)) {
                            // If it's an object with an 'id' property, use that
                            if ('id' in value) {
                                value = value.id
                            } else if ('value' in value) {
                                value = value.value
                            } else {
                                console.warn('Invalid filter value (unexpected object):', value)
                                return null
                            }
                        }

                        // Handle sentinel value for empty/null filtering
                        const isEmptySentinel = value === '__EMPTY__'
                        if (isEmptySentinel) {
                            value = ''
                        }

                        // Skip filters with empty values unless it's the intentional empty sentinel
                        if (!isEmptySentinel && (value === '' || value === null || value === undefined)) {
                            return null
                        }

                        // Convert dates to GMT ISO string
                        if (constraint.matchMode === FilterMatchMode.DATE_IS ||
                            constraint.matchMode === FilterMatchMode.DATE_IS_NOT ||
                            constraint.matchMode === FilterMatchMode.DATE_BEFORE ||
                            constraint.matchMode === FilterMatchMode.DATE_AFTER) {
                            value = (forceToGmt(value)).toISOString()
                        }
                        // Uppercase string values (but not empty strings or sentinel values)
                        else if (typeof value === 'string' && value !== '' && value !== '__EMPTY__') {
                            value = value.toUpperCase()
                        }

                        // Force equals operator for __EMPTY__ sentinel (null/empty check requires equals)
                        if (isEmptySentinel) {
                            return `${field} equals __EMPTY__`
                        }
                        return `${field} ${constraint.matchMode} ${value}`
                    }

                    return null
                })
                .filter(condition => condition !== null)

            // If there are multiple constraints for this field, wrap them in parentheses
            if (fieldConditions.length > 1) {
                return `(${fieldConditions.join(operator)})`
            } else if (fieldConditions.length === 1) {
                return fieldConditions[0]
            }

            return null
        })
        .filter(condition => condition !== null)
        .join(' and ')  // Join different fields with AND

    return result || null
}
